import {
  Grid,
  GridItem,
  Box,
  Text,
  HStack,
  VStack,
  Icon,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Spinner,
  IconButton,
  Image,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useGetLoggedInUserData, useLogoutMutation } from '@/services/auth/auth';
import { useStore } from '@/store/store';
import { useEffect } from 'react';
import { IUser } from '@/services/auth/types';
import SideBar from '@/components/Sidebar';
import logoutIcon from '../../assets/svg/logout.svg?react';
import ProfileIcon from '../../assets/svg/profile.svg?react';
import Support from '../../assets/svg/support.svg?react';
import BuildingIcon from '../../assets/svg/building.svg?react';
import LeftArrowIcon from '../../assets/svg/left.svg?react'; // Add this import
import { AxiosError } from 'axios';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import useErrorHandler from '@/utils/errorHandler';
import useCustomToast from '@/components/common/Toast';
import Chat from '@/components/common/Chat';
import Notification from '@/components/Notifications';
import useNotifications from '@/hooks/useNotification';

// Sample menuItems array for redirection logic
const menuItems = [
  { path: NAVIGATION_ROUTES.DASHBOARD, label: 'Dashboard', icon: null },
  { path: NAVIGATION_ROUTES.IMAGECROPPER, label: 'Image Optimize', icon: null },
  { path: NAVIGATION_ROUTES.LEARNING_RESOURCES, label: 'Learning Resources', icon: null },
  { path: NAVIGATION_ROUTES.ADD_PRODUCT, label: 'Add Product', icon: null },
  { path: NAVIGATION_ROUTES.MANAGE_PRODUCT, label: 'Manage Product', icon: null },
  { path: NAVIGATION_ROUTES.ALL_ORDERS, label: 'All Orders', icon: null },
  { path: NAVIGATION_ROUTES.RETURN_ORDERS, label: 'Return Orders', icon: null },
  { path: NAVIGATION_ROUTES.REVIEWS, label: 'Reviews', icon: null },
  { path: NAVIGATION_ROUTES.VOUCHER_MANAGEMENT, label: 'Voucher Management', icon: null },
  { path: NAVIGATION_ROUTES.REPORTS_AND_METRICS, label: 'Reports and Metrics', icon: null },
  { path: NAVIGATION_ROUTES.USER_MANAGEMENT, label: 'User Management', icon: null },
  { path: NAVIGATION_ROUTES.SETTINGS, label: 'Settings', icon: null },
  { path: NAVIGATION_ROUTES.TOC, label: 'Terms and Conditions', icon: null },
];

// Role-based access control logic
const hasAccess = (user: IUser | null, path: string): boolean => {
  if (!user) return false;

  if (path === NAVIGATION_ROUTES.SETTINGS) return true;

  if (user.role === 'vendor') return true;

  const isAdmin = user.vendor_role_permissions?.some((role) =>
    role.permissions.some((perm) => perm.role_name === 'Admin'),
  );
  if (isAdmin) return true;

  const userRoles =
    user.vendor_role_permissions?.flatMap((role) =>
      role.permissions.map((perm) => perm.role_name),
    ) || [];

  if (userRoles.includes('Chat Management') && userRoles.length === 1) {
    return false;
  }

  const roleAccess: { [key: string]: string[] } = {
    'Review Management': [NAVIGATION_ROUTES.REVIEWS],
    'Coupon Management': [NAVIGATION_ROUTES.VOUCHER_MANAGEMENT],
    'Dashboard Management': [NAVIGATION_ROUTES.DASHBOARD],
    'Order Management': [NAVIGATION_ROUTES.ALL_ORDERS, NAVIGATION_ROUTES.RETURN_ORDERS],
    'Product Management': [NAVIGATION_ROUTES.ADD_PRODUCT, NAVIGATION_ROUTES.MANAGE_PRODUCT],
    'Chat Management': [],
    Admin: [
      NAVIGATION_ROUTES.SETTINGS,
      NAVIGATION_ROUTES.USER_MANAGEMENT,
      NAVIGATION_ROUTES.REPORTS_AND_METRICS,
    ],
  };

  return userRoles.some((role) => roleAccess[role]?.includes(path));
};

const hasChatAccess = (user: IUser | null): boolean => {
  if (!user) return false;
  if (user.role === 'vendor') return true;
  const isAdmin = user.vendor_role_permissions?.some((role) =>
    role.permissions.some((perm) => perm.role_name === 'Admin'),
  );
  if (isAdmin) return true;
  const userRoles =
    user.vendor_role_permissions?.flatMap((role) =>
      role.permissions.map((perm) => perm.role_name),
    ) || [];
  return userRoles.includes('Chat Management');
};

const routeTitles: Record<string, string> = {
  [NAVIGATION_ROUTES.DASHBOARD]: 'Dashboard',
  [NAVIGATION_ROUTES.IMAGECROPPER]: 'Image Optimize',
  [NAVIGATION_ROUTES.LEARNING_RESOURCES]: 'Learning Resource',
  [NAVIGATION_ROUTES.ALL_ORDERS]: 'All Orders',
  [NAVIGATION_ROUTES.RETURN_ORDERS]: 'Return Orders',
  [NAVIGATION_ROUTES.REVIEWS]: 'Reviews',
  [NAVIGATION_ROUTES.VOUCHER_MANAGEMENT]: 'Voucher Management',
  [NAVIGATION_ROUTES.REPORTS_AND_METRICS]: 'Reports And Metrics',
  [NAVIGATION_ROUTES.USER_MANAGEMENT]: 'User Management',
  [NAVIGATION_ROUTES.MANAGE_PRODUCT]: 'Manage Product',
  [NAVIGATION_ROUTES.ADD_PRODUCT]: 'Add Product',
  [NAVIGATION_ROUTES.SETTINGS]: 'Settings',
  [NAVIGATION_ROUTES.TOC]: 'Terms and Conditions',
  [NAVIGATION_ROUTES.PAYMENT_DETAILS]: 'Payment Details',
};

const MainLayout = () => {
  const { data: loggedInUser } = useGetLoggedInUserData();
  const { mutateAsync: logOutUser, isLoading } = useLogoutMutation();
  const { user, setUser, clearUser } = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const { handleError } = useErrorHandler();
  const { showToast } = useCustomToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  useNotifications();
  useEffect(() => {
    if (loggedInUser) {
      setUser(loggedInUser as IUser);
    }
  }, [loggedInUser, setUser]);

  const getDynamicTitle = (pathname: string) => {
    const titleFromRoute = routeTitles[pathname];
    if (titleFromRoute) return titleFromRoute;
    if (pathname.includes('view-product')) return 'View Product';
    if (pathname.includes('reviews')) return 'Reviews';
    if (pathname.includes('edit-product')) return 'Edit Product';
    if (pathname.includes('settings')) return 'Settings';
    return 'Dashboard';
  };

  const currentTitle = getDynamicTitle(location.pathname);

  // Check if the back button should be shown (for routes not in menuItems)
  const shouldShowBackButton = () => {
    return !menuItems.some((item) => item.path === location.pathname);
  };

  // Handle back navigation
  const handleBack = () => {
    if (location.state?.returnPath) {
      navigate(location.state.returnPath, { state: location.state });
    } else {
      navigate(-1); // Go back one page in history
    }
  };

  useEffect(() => {
    if (!user) return;

    const userRoles =
      user.vendor_role_permissions?.flatMap((role) =>
        role.permissions.map((perm) => perm.role_name),
      ) || [];
    if (userRoles.includes('Chat Management') && userRoles.length === 1) {
      if (location.pathname !== NAVIGATION_ROUTES.DASHBOARD) {
        navigate(NAVIGATION_ROUTES.DASHBOARD);
        showToast('warning', 'Chat Management users can only access the chat feature');
      }
      return;
    }

    if (!hasAccess(user, location.pathname)) {
      const firstAccessibleRoute = menuItems.find((item) => hasAccess(user, item.path))?.path;
      if (firstAccessibleRoute && location.pathname !== firstAccessibleRoute) {
        navigate(firstAccessibleRoute);
      } else if (!firstAccessibleRoute) {
        navigate(NAVIGATION_ROUTES.UNAUTHORIZED);
      }
    }
  }, [user, location.pathname, navigate, showToast]);

  const handleLogout = async () => {
    try {
      await logOutUser().then(() => {
        clearUser();
        navigate(NAVIGATION_ROUTES.SIGNIN);
        showToast('success', 'Logout Successful');
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        handleError(error);
      }
    }
  };

  return (
    <>
      <Grid
        templateColumns={{
          base: '1fr',
          lg: '267px 1fr',
        }}
        height="100vh"
      >
        <GridItem
          display={{ base: 'none', lg: 'flex' }}
          color="white"
          flexDirection="column"
          justifyContent="space-between"
          borderRight="1px solid"
          borderColor="border.default"
          zIndex="1"
        >
          <SideBar />
        </GridItem>

        <GridItem display="grid" gridTemplateRows="auto 1fr" overflow="hidden">
          <Box
            py={4}
            px={6}
            borderBottom="1px solid"
            borderColor="border.default"
            bg="background.white"
            position="sticky"
            top="0"
            zIndex="10"
          >
            <Flex justifyContent="space-between" alignItems="center">
              {/* Mobile Header */}
              <Flex display={{ base: 'flex', lg: 'none' }} alignItems="center" flex="1">
                <IconButton
                  aria-label="Open menu"
                  icon={<HamburgerIcon />}
                  onClick={onOpen}
                  variant="ghost"
                  mr={2}
                  size="sm"
                />
                {shouldShowBackButton() && (
                  <IconButton
                    aria-label="Back to previous page"
                    icon={<LeftArrowIcon />}
                    onClick={handleBack}
                    variant="ghost"
                    width="24px"
                    height="24px"
                    size="sm"
                    mr={2}
                  />
                )}
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  fontFamily="primary"
                  color="text.primary"
                  display={{ base: 'block', lg: 'none' }}
                >
                  {currentTitle}
                </Text>
              </Flex>

              {/* Desktop Header */}
              <Flex display={{ base: 'none', lg: 'flex' }} alignItems="center" gap={3}>
                {shouldShowBackButton() && (
                  <IconButton
                    aria-label="Back to previous page"
                    icon={<LeftArrowIcon />}
                    onClick={handleBack}
                    variant="ghost"
                    width="24px"
                    height="24px"
                    mr={2}
                  />
                )}
                <Text fontSize="xl" fontWeight="semibold" fontFamily="primary" color="text.primary">
                  {currentTitle}
                </Text>
              </Flex>

              {/* User Menu and Notifications */}
              <HStack gap={{ base: 4, lg: 6 }} flexShrink={0}>
                <Box minW="40px">
                  <Notification />
                </Box>
                <HStack gap="10px" fontFamily="primary">
                  <Menu>
                    <MenuButton>
                      <Box
                        boxSize="40px"
                        borderRadius="full"
                        overflow="hidden"
                        bg="#71B58E"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="white"
                        fontWeight="semibold"
                        fontFamily="primary"
                        lineHeight="normal"
                      >
                        {user?.photo_small ? (
                          <Image
                            src={user?.photo_small}
                            alt={user?.email || 'User'}
                            objectFit="cover"
                            width="100%"
                            height="100%"
                          />
                        ) : (
                          <span style={{ fontSize: '16px' }}>
                            {(user?.first_name?.charAt(0) ?? '') +
                              (user?.last_name?.charAt(0) ?? '')}
                          </span>
                        )}
                      </Box>
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        fontSize="sm"
                        fontWeight="semibold"
                        fontFamily="primary"
                        color="text.primary"
                        display="flex"
                        alignItems="center"
                        gap="2"
                        _hover={{ background: 'background.tertiary' }}
                        onClick={() =>
                          navigate(NAVIGATION_ROUTES.SETTINGS, {
                            state: { tabIndex: 0 },
                          })
                        }
                      >
                        <Icon as={ProfileIcon} color="status.error" boxSize={4} mb="3px" />
                        <Text color="text.secondary">Account Settings</Text>
                      </MenuItem>
                      {user?.role === 'vendor' && (
                        <MenuItem
                          fontSize="sm"
                          fontWeight="semibold"
                          fontFamily="primary"
                          color="text.primary"
                          display="flex"
                          alignItems="center"
                          gap="2"
                          _hover={{ background: 'background.tertiary' }}
                          onClick={() =>
                            navigate(NAVIGATION_ROUTES.SETTINGS, {
                              state: { tabIndex: 1 },
                            })
                          }
                        >
                          <Icon as={BuildingIcon} color="status.error" boxSize={4} mb="3px" />
                          <Text color="text.secondary">Store Settings</Text>
                        </MenuItem>
                      )}
                      <MenuItem
                        fontSize="sm"
                        fontWeight="semibold"
                        fontFamily="primary"
                        color="text.primary"
                        display="flex"
                        alignItems="center"
                        gap="2"
                        _hover={{ background: 'background.tertiary' }}
                        onClick={() => navigate(NAVIGATION_ROUTES.TOC)} // Correct
                      >
                        <Icon as={Support} color="status.error" boxSize={4} mb="3px" />
                        <Text color="text.secondary">Terms and Conditions</Text>
                      </MenuItem>
                      <MenuItem
                        fontSize="sm"
                        fontWeight="semibold"
                        fontFamily="primary"
                        color="status.error"
                        onClick={handleLogout}
                        display="flex"
                        alignItems="center"
                        gap="2"
                        _hover={{ background: 'background.tertiary' }}
                      >
                        {isLoading ? (
                          <Flex justifyContent="center" alignItems="center" w="full">
                            <Spinner size="xs" color="status.error" />
                          </Flex>
                        ) : (
                          <>
                            <Icon as={logoutIcon} color="status.error" boxSize={4} mb="3px" />
                            <Text color="status.error">LogOut</Text>
                          </>
                        )}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                  <VStack align="start" spacing={0}>
                    <Text
                      fontWeight="semibold"
                      color="text.primary"
                      fontSize="sm"
                      textOverflow={{ base: 'ellipsis', lg: 'clip' }}
                      overflow={{ base: 'hidden', lg: 'visible' }}
                      whiteSpace={{ base: 'nowrap', lg: 'normal' }}
                      maxW={{ base: '70px', lg: 'none' }}
                      display={{ base: 'none', lg: 'block' }}
                    >
                      {user?.first_name && user?.last_name
                        ? user.first_name + ' ' + user.last_name
                        : 'Vendor'}
                    </Text>
                    <Text
                      color="text.secondary"
                      fontSize="xs"
                      textOverflow={{ base: 'ellipsis', lg: 'clip' }}
                      overflow={{ base: 'hidden', lg: 'visible' }}
                      whiteSpace={{ base: 'nowrap', lg: 'normal' }}
                      maxW={{ base: '70px', lg: 'none' }}
                      display={{ base: 'none', lg: 'block' }}
                    >
                      {user?.email ?? 'user@gmail.com'}
                    </Text>
                  </VStack>
                </HStack>
              </HStack>
            </Flex>
          </Box>

          <Box overflowY="auto" pb={4} bg="background.secondary" zIndex="1" overflowX="hidden">
            <Outlet />
          </Box>
        </GridItem>

        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent>
            <DrawerBody p={0}>
              <SideBar closeDrawer={onClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Grid>

      {hasChatAccess(user) && <Chat />}
    </>
  );
};

export default MainLayout;
